'use client';

import { ArticleCard } from '@/sanity/fetchArticles';
import HIHImage from './HIHImage';
import Tag from './Tag';
import HIHLink from './Link';
import { URL_PATH_ARTICLE, URL_PATH_CATEGORY } from '@/shared/constants';
import ActionFavorite from '../action/favorite/ActionFavorite';
import {
  MATOMO_CONTENT_INTERACTIONS,
  MATOMO_CONTENT_KINDS,
  buildContentPiece,
} from '@/utils/matomo';
import { useContentImpression } from '@/hooks/useContentImpression';
import { ContentBlockProvider } from './ContentTracking';
import styles from './SingleProminentArticleCard.module.scss';

interface SingleArticleCardProps {
  article: ArticleCard;
  priority?: boolean;
}

export default function SingleProminentArticleCard({
  article,
  priority = false,
}: SingleArticleCardProps) {
  const categories = article.categories?.slice(0, 3);
  const articlePath = `/${URL_PATH_ARTICLE}/${article.slug.current}`;

  // Trackt nur innerhalb eines ContentTrackingModule, sonst No-Op – siehe ContentTracking.
  const { ref, block, trackInteraction } = useContentImpression({
    contentPiece: buildContentPiece(MATOMO_CONTENT_KINDS.GUIDE, article.slug.current),
    contentTarget: articlePath,
  });

  const handleOpenClick = () =>
    trackInteraction(MATOMO_CONTENT_INTERACTIONS.DETAILSEITE_GEOEFFNET);

  return (
    <ContentBlockProvider block={block}>
      <div ref={ref} className={styles.singleProminentArticleCard}>
        <div className={styles.imageWrapper}>
          <HIHLink
            key={article._id}
            href={articlePath}
            onClick={handleOpenClick}
            className={styles.imageLink}
          >
            <HIHImage
              image={article.coverImage}
              fill
              sizes="(min-width: 768px) 50vw, 100vw"
              priority={priority}
              className={styles.image}
            />
          </HIHLink>
          <ActionFavorite
            id={article._id}
            className={styles.favoriteButton}
            bookmarkType="Guide"
          ></ActionFavorite>
        </div>
        <div className={styles.bottomHalf}>
          <div className={styles.tags}>
            {categories?.map((category) => (
              <Tag
                key={category._id}
                label={category.name}
                size="sm"
                href={`/${URL_PATH_CATEGORY}/${category.slug?.current}`}
              />
            ))}
          </div>
          <HIHLink key={article._id} href={articlePath} onClick={handleOpenClick}>
            <div className={styles.title}>{article.title}</div>
            <div className={styles.description}>{article.shortDescription}</div>
          </HIHLink>
        </div>
      </div>
    </ContentBlockProvider>
  );
}
